import {Component, ElementRef, EventEmitter, OnInit, Output, ViewChild} from '@angular/core';
import {FormService} from "@core/shared/services/form.service";
import {Offer} from "@core/shared/models/offer";
import {ActivatedRoute} from "@angular/router";
import {AbstractControl, FormBuilder, FormGroup, UntypedFormArray, UntypedFormControl, UntypedFormGroup, Validators} from "@angular/forms";
import {OfferService} from "@core/shared/services/offer.service";
import {OfferPriceLevel} from "@core/shared/models/offer/offer-price-level";
import {REGEX_PRICE} from "@core/shared/models/regex";
import {OFFER_PRICE_LEVEL_TYPES, OfferPriceLevelType, OfferPriceLevelTypeType} from "@core/shared/models/offer/offer-price/offer-price-level/offer-price-level-type";
import {OFFER_PRICE_LEVEL_INCREASE_AREAS, OfferPriceLevelIncreaseArea} from "@core/shared/models/offer/offer-price/offer-price-level/offer-price-level-increase-area";
import {OfferPriceLevelService} from "@core/shared/services/offer/offer-price-level.service";

@Component({
    selector: 'app-core-offer-price-level-form',
    templateUrl: './offer-price-level-form.component.html',
    styleUrl: './offer-price-level-form.component.scss',
    providers: [
        FormService
    ]
})
export class OfferPriceLevelFormComponent implements OnInit {

    @Output() close: EventEmitter<void> = new EventEmitter<void>();

    @Output() priceLevelCollectionUpdated: EventEmitter<void> = new EventEmitter<void>();

    @ViewChild('formReference', { static: false }) formReference: ElementRef<HTMLDivElement>;

    public offer: Offer;

    constructor(
        private _activatedRoute: ActivatedRoute,
        private _formBuilder: FormBuilder,
        private _offerService: OfferService,
        private _offerPriceLevelService: OfferPriceLevelService,
        public formService: FormService
    ) {
    }

    ngOnInit() {

        this._initForm();

        this._offerService.getItemAPI(parseInt((this._activatedRoute.snapshot.params as { id: string }).id)).subscribe((offer: Offer): void => {

            this.offer = offer;

            this._initItems();

            if(!Boolean(this.priceLevelsControl.controls.length)){

                this.addPriceLevel();
            }
        });
    }

    private _initForm(): void {

        this.formService.form = this._formBuilder.group({
            priceLevels: new UntypedFormArray([])
        });

        this.formService.submitCallback = (): void => {

            if (this.form.invalid) {

                return;
            }

            const formValue = {...this.form.getRawValue()};

            const data: object = Object.assign(formValue, {
                priceLevels: (this.priceLevelsControl.value as OfferPriceLevel[]).map((price: OfferPriceLevel): OfferPriceLevel => {

                    const clone: OfferPriceLevel = {...price};

                    return Object.assign(clone, {
                        value: clone.type === 'price' ? (clone.value * 100) : (clone.value / 100),
                        additionalValue: !!clone.additionalValue ? (clone.type === 'price' ? (clone.additionalValue * 100) : (clone.additionalValue / 100)) : null
                    });
                })
            });

            this._offerPriceLevelService.updateOfferItemsAPI(this.offer.id, data).subscribe((): void => {

                this.priceLevelCollectionUpdated.emit();
            });
        };
    }

    private _initItems(): void {

        this.offer.priceLevels.forEach((offerPriceLevel: OfferPriceLevel): void => {

            this.priceLevelsControl.push(this.createPriceLevelGroup({
                id: offerPriceLevel.id,
                name: offerPriceLevel.name,
                type: offerPriceLevel.type,
                min: offerPriceLevel.min,
                max: offerPriceLevel.max,
                priceIncreaseArea: offerPriceLevel.priceIncreaseArea,
                value: offerPriceLevel.value,
                additionalValue: offerPriceLevel.additionalValue
            }));
        });
    }

    public createPriceLevelGroup(data: OfferPriceLevel): UntypedFormGroup {

        const control: UntypedFormGroup = this._formBuilder.group({
            name: [data.name, Validators.required],
            type: [data.type, Validators.required],
            priceIncreaseArea: [data.priceIncreaseArea, [Validators.required]],
            min: [data.min, [Validators.required, Validators.pattern(/^[0-9]*$/)]],
            max: [data.max, [Validators.required, Validators.pattern(/^[0-9]*$/)]],
            value: [data.value ? (data.type === 'price' ? (data.value / 100) : (data.value * 100)) : null, [Validators.pattern(REGEX_PRICE), Validators.required]],
            additionalValue: [data.additionalValue ? (data.type === 'price' ? (data.additionalValue / 100) : (data.additionalValue * 100)) : null, [Validators.pattern(REGEX_PRICE), (control: UntypedFormControl) => {

                if (!control.parent) {

                    return null;
                }

                if (control.parent.get('type').invalid) {

                    return null;
                }

                if ((control.parent.get('type').value as OfferPriceLevelTypeType) === 'percent') {

                    return null;
                }

                if (control.value && (control.value.length > 0)) {

                    return null;
                }

                return {
                    'isRequired': {
                        valid: false
                    }
                };
            }]]
        });

        if (!!data.id) {

            control.addControl('id', this._formBuilder.control(data.id));
        }

        return control;
    }

    public addPriceLevel(): void {

        this.priceLevelsControl.push(this.createPriceLevelGroup({
            id: null,
            name: null,
            type: null,
            min: null,
            max: null,
            priceIncreaseArea: null,
            value: null,
            additionalValue: null
        }));

        setTimeout((): void => {

            this.scrollToBottomOfElement(this.formReference);
        });
    }

    public removePriceLevel(index: number) {

        this.priceLevelsControl.removeAt(index);
    }

    public handlePriceLevelValues(control: AbstractControl): void {

        control.get('value').patchValue(null);

        control.get('value').updateValueAndValidity();

        control.get('additionalValue').patchValue(null);

        control.get('additionalValue').updateValueAndValidity();
    }

    public getPriceLevelControl(index: number): AbstractControl {

        return this.priceLevelsControl.controls[index];
    }

    public indexAsString(index: number): string{

        return index.toString();
    }

    public scrollToBottomOfElement(element: ElementRef): void {

        if(!element || !element.nativeElement){

            return;
        }

        element.nativeElement.scrollIntoView({
            behavior: "smooth",
            block: "end",
            inline: "nearest"
        });
    }

    get form(): FormGroup {

        return this.formService.form;
    }

    get priceLevelsControl(): UntypedFormArray {

        return this.form.get('priceLevels') as UntypedFormArray;
    }

    get offerPriceLevelTypes(): OfferPriceLevelType[] {

        return OFFER_PRICE_LEVEL_TYPES;
    }

    get offerPriceLevelIncreaseAreas(): OfferPriceLevelIncreaseArea[] {

        return OFFER_PRICE_LEVEL_INCREASE_AREAS;
    }
}
